import config from '../../config';
import { getAuthHeaders, getUserIdFromToken, handleResponse, handleNoContentResponse } from './common';
import clientApi from './clientApi';
import clientIntakeApi from './clientIntakeApi';
import visitApi from './visitApi';
import messageApi from './messageApi';
import permissionApi from './permissionApi';
import transferApi from './transferApi';
import adminApi from './adminApi';
import ticketApi from './ticketApi';
import noteTemplatesApi from './noteTemplatesApi';
import templateRenderingApi from './templateRenderingApi';
import billingApi from './billingApi';
import invoiceApi from './invoiceApi';
import reportsApi from './reportsApi';
import stripeConnectApi from './stripeConnectApi';
import letterTemplatesApi from './letterTemplatesApi';
import messageTemplatesApi from './messageTemplatesApi';
import chartLockApi from './chartLockApi';
import organizationUsersApi from './organizationUsersApi';
import { normalizeOrganizationInviteResult } from '../organizationInviteMessages';
import baaApi from './baaApi';
import ultrasoundOrderApi from './ultrasoundOrderApi';
import releasePacketApi from './releasePacketApi';
import insuranceApi from './insuranceApi';
import superbillApi from './superbillApi';
import clientChartImportApi from './clientChartImportApi';
import mrnSettingsApi from './mrnSettingsApi';
import appointmentDurationSettingsApi from './appointmentDurationSettingsApi';
import customAppointmentTypesApi from './customAppointmentTypesApi';
import formsApi from './formsApi';
import medicationApi from './medicationApi';
import practiceExtensionsApi from './practiceExtensionsApi';
import clientVisitSnapshotsApi from './clientVisitSnapshotsApi';
import { deriveStructuredMedicalProfileStatus } from './medicalProfileStatus';
import { logMedicalHistoryDebug } from '../medicalHistoryDebug';
export { deriveStructuredMedicalProfileStatus, hasMeaningfulMedicalProfileData } from './medicalProfileStatus';

const NONE_LIKE_MEDICATION_RESPONSES = new Set([
    'n/a',
    'na',
    'no',
    'none',
    'not applicable',
    'no meds',
    'no medications',
    'no current meds',
    'no current medications',
    'none reported',
]);

const MEDICATION_INTAKE_SOURCES = [
    { key: 'rxMedications', label: 'Prescription medications' },
    { key: 'otcMedications', label: 'Over-the-counter medications' },
    { key: 'herbsSupplements', label: 'Herbs, supplements & vitamins' },
];

const MEDICATION_RECORD_TYPE_LABELS = {
    rx: 'Prescription medications',
    otc: 'Over-the-counter medications',
    supplement: 'Herbs, supplements & vitamins',
};

const normalizeMedicationText = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[.,;:]+$/g, '')
        .replace(/\s+/g, ' ');

const isNoneLikeMedicationText = (value) => {
    const normalized = normalizeMedicationText(value);
    return !normalized || NONE_LIKE_MEDICATION_RESPONSES.has(normalized);
};

const splitMedicationIntakeEntries = (value) => {
    if (isNoneLikeMedicationText(value)) return [];

    return String(value || '')
        .split(/[\n;]+/)
        .flatMap((chunk) => {
            const trimmed = chunk.trim();
            if (!trimmed) return [];
            const commaParts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);

            if (
                commaParts.length > 1 &&
                commaParts.slice(1).every((part) => !/^(?:\d|once|twice|three times|four times|daily|nightly|weekly|monthly|as needed|prn|bid|tid|qid|qhs|q\d+h|every\b|with\b|before\b|after\b)/i.test(part))
            ) {
                return commaParts;
            }

            return [trimmed];
        })
        .map((entry) => entry.replace(/^[-*•\d.)\s]+/, '').trim())
        .filter((entry) => !isNoneLikeMedicationText(entry));
};

const extractMedicationDetails = (entry) => {
    const normalizedEntry = String(entry || '').trim();
    const dosageMatch = normalizedEntry.match(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|g|grams?|ml|mL|units?|iu|tabs?|tablets?|caps?|capsules?|drops?|puffs?)\b/i);
    const frequencyMatch = normalizedEntry.match(/\b(?:once|twice|three times|four times|daily|nightly|weekly|monthly|as needed|prn|bid|tid|qid|qhs|q\d+h|every\s+\d+\s+(?:hours?|days?|weeks?))\b.*$/i);
    const dosage = dosageMatch?.[0] || null;
    const frequency = frequencyMatch?.[0] || null;

    let name = normalizedEntry;
    const firstDetailIndex = [dosageMatch?.index, frequencyMatch?.index]
        .filter((index) => typeof index === 'number')
        .sort((a, b) => a - b)[0];

    if (typeof firstDetailIndex === 'number' && firstDetailIndex > 0) {
        name = normalizedEntry.slice(0, firstDetailIndex).replace(/[-–—,]+$/g, '').trim();
    }

    return {
        name: name || normalizedEntry,
        dosage,
        frequency,
    };
};

const getMedicationName = (medication) =>
    medication?.name ||
    medication?.Name ||
    medication?.customMedicationName ||
    medication?.CustomMedicationName ||
    medication?.medicationName ||
    medication?.MedicationName ||
    '';

const toDateOnlyString = (value) => {
    if (!value) return null;
    const s = String(value).trim();
    return s ? s.split('T')[0] : null;
};

const toOptionalNumber = (value) => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const medicationDedupeKey = (medication) => normalizeMedicationText([
    getMedicationName(medication),
    medication?.dosage ?? medication?.Dosage ?? '',
    medication?.frequency ?? medication?.Frequency ?? '',
].filter(Boolean).join('|'));

const buildMedicationPayloadsFromIntake = (medicationIntake, existingMedications = []) => {
    const seen = new Set((Array.isArray(existingMedications) ? existingMedications : []).map(medicationDedupeKey).filter(Boolean));
    const structuredPayloads = (Array.isArray(medicationIntake?.medicationRecords) ? medicationIntake.medicationRecords : [])
        .map((record) => {
            const name = String(record?.name || '').trim();
            if (!name) return null;
            const sourceLabel = MEDICATION_RECORD_TYPE_LABELS[record.type] || 'Medical history intake';
            const payload = {
                MedicationId: null,
                CustomMedicationName: name,
                Dosage: record.dosage || null,
                Frequency: record.frequency || null,
                Route: null,
                StartDate: null,
                EndDate: null,
                PrescribedBy: null,
                Indication: null,
                Notes: [
                    `Imported from medical history intake (${sourceLabel})`,
                    record.notes ? `Client notes: ${record.notes}` : null,
                ].filter(Boolean).join('. '),
                Lot: null,
                ExpirationDate: null,
            };
            const keyValue = medicationDedupeKey(payload);
            if (!keyValue || seen.has(keyValue)) return null;
            seen.add(keyValue);
            return payload;
        })
        .filter(Boolean);

    const textPayloads = MEDICATION_INTAKE_SOURCES.flatMap(({ key, label }) =>
        splitMedicationIntakeEntries(medicationIntake?.[key]).map((entry) => {
            const details = extractMedicationDetails(entry);
            const payload = {
                MedicationId: null,
                CustomMedicationName: details.name,
                Dosage: details.dosage,
                Frequency: details.frequency,
                Route: null,
                StartDate: null,
                EndDate: null,
                PrescribedBy: null,
                Indication: null,
                Notes: `Imported from medical history intake (${label}): ${entry}`,
                Lot: null,
                ExpirationDate: null,
            };
            const keyValue = medicationDedupeKey(payload);
            if (!keyValue || seen.has(keyValue)) return null;
            seen.add(keyValue);
            return payload;
        }).filter(Boolean)
    );

    return [...structuredPayloads, ...textPayloads];
};

const api = {
    ...baaApi,
    ...organizationUsersApi,
    ...ultrasoundOrderApi,
    ...clientApi,
    ...clientIntakeApi,
    ...visitApi,
    ...messageApi,
    ...permissionApi,
    ...transferApi,
    ...adminApi,
    ...ticketApi,
    ...noteTemplatesApi,
    ...billingApi,
    ...invoiceApi,
    reportsApi,
    stripeConnect: stripeConnectApi,
    templateRendering: templateRenderingApi,
    letterTemplates: letterTemplatesApi,
    messageTemplates: messageTemplatesApi,
    chartLock: chartLockApi,
    releasePacket: releasePacketApi,
    insurance: insuranceApi,
    superbill: superbillApi,
    clientChartImport: clientChartImportApi,
    mrnSettings: mrnSettingsApi,
    appointmentDurationSettings: appointmentDurationSettingsApi,
    customAppointmentTypes: customAppointmentTypesApi,
    forms: formsApi,
    practiceExtensions: practiceExtensionsApi,
    clientVisitSnapshots: clientVisitSnapshotsApi,

    // User APIs
    fetchCurrentUser: async () => {
        const userId = getUserIdFromToken();
        if (!userId) throw new Error('No user ID found in token');

        const res = await fetch(`${config.api.url}/user/${userId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // User Settings APIs
    fetchUserData: async (userId) => {
        const targetUserId = userId || getUserIdFromToken();
        if (!targetUserId) throw new Error('No user ID found');

        const res = await fetch(`${config.api.url}/user/${targetUserId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    disable2FA: async (userId) => {
        const targetUserId = userId || getUserIdFromToken();
        if (!targetUserId) throw new Error('No user ID found');

        const res = await fetch(`${config.api.url}/user/${targetUserId}/disable-2fa`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    enable2FA: async (userId) => {
        const targetUserId = userId || getUserIdFromToken();
        if (!targetUserId) throw new Error('No user ID found');
        const res = await fetch(`${config.api.url}/user/${targetUserId}/enable-2fa`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    update2FAMethod: async (method, userId) => {
        const targetUserId = userId || getUserIdFromToken();
        if (!targetUserId) throw new Error('No user ID found');

        const res = await fetch(`${config.api.url}/user/${targetUserId}/2fa-method`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify({ preferred2FAMethod: method })
        });
        return handleResponse(res);
    },

    updatePassword: async (currentPassword, newPassword, userId) => {
        const targetUserId = userId || getUserIdFromToken();
        if (!targetUserId) throw new Error('No user ID found');

        const res = await fetch(`${config.api.url}/user/${targetUserId}/change-password`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                currentPassword,
                newPassword
            })
        });

        if (!res.ok) {
            const errorText = await res.text();
            let data = {};
            try {
                data = errorText ? JSON.parse(errorText) : {};
            } catch {
                data = {};
            }

            const validationErrors = data?.errors
                ? Object.values(data.errors).flat().filter(Boolean).join(' ')
                : '';
            const msg = data?.message || validationErrors || data?.detail || data?.title || errorText || `Failed to update password: ${res.status}`;
            throw new Error(msg);
        }
        return handleResponse(res);
    },

    // PHI Acknowledgement APIs
    getPhiAcknowledgement: async () => {
        const userId = getUserIdFromToken();
        if (!userId) throw new Error('No user ID found in token');
        
        const res = await fetch(`${config.api.url}/user/${userId}/phi-acknowledgement`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    recordPhiAcknowledgement: async () => {
        const userId = getUserIdFromToken();
        if (!userId) throw new Error('No user ID found in token');
        
        const res = await fetch(`${config.api.url}/user/${userId}/phi-acknowledgement`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Portal APIs
    sendPortalInvite: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/send-portal-invite`, {
            method: "POST",
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    resendPortalInvite: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/resend-portal-invite`, {
            method: "POST",
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getClientPortalInvites: async (organizationId, status) => {
        const url = new URL(`${config.api.url}/organization/${organizationId}/client-portal-invites`);
        if (status && status !== 'all') url.searchParams.set('status', status);
        const res = await fetch(url.toString(), { headers: getAuthHeaders() });
        return handleResponse(res);
    },

    // Organization APIs
    getMyOrganization: async () => {
        const res = await fetch(`${config.api.url}/organization/my`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getOrganization: async (organizationId) => {
        const res = await fetch(`${config.api.url}/organization/${organizationId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createOrganization: async (orgData) => {
        const res = await fetch(`${config.api.url}/organization`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(orgData)
        });
        return handleResponse(res);
    },

    updateOrganization: async (organizationId, orgData) => {
        const res = await fetch(`${config.api.url}/organization/${organizationId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(orgData)
        });
        return handleNoContentResponse(res);
    },

    uploadOrganizationLogo: async (organizationId, imageFile) => {
        if (!organizationId) throw new Error('Organization ID is required');
        if (!imageFile) throw new Error('Image file is required');

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!validTypes.includes(imageFile.type)) {
            throw new Error('Invalid file type. Only JPG, PNG, GIF, WebP, and SVG images are allowed.');
        }

        const maxSize = 5 * 1024 * 1024;
        if (imageFile.size > maxSize) {
            throw new Error('File size exceeds 5MB limit.');
        }

        const formData = new FormData();
        formData.append('file', imageFile);

        const token = localStorage.getItem('velaToken');
        const res = await fetch(`${config.api.url}/organization/${organizationId}/logo`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });

        if (!res.ok) {
            const errorText = await res.text();
            throw new Error(errorText || `Failed to upload logo: ${res.status}`);
        }

        if (res.status === 204) return { success: true };

        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return res.json();
        }

        const text = await res.text();
        if (text) {
            try { return JSON.parse(text); } catch { /* not JSON */ }
            return { logoUrl: text.trim(), success: true };
        }

        return { success: true };
    },

    getOrganizationUsers: async (organizationId) => {
        const url = `${config.api.url}/organization/${organizationId}/users`;

        const res = await fetch(url, {
            headers: getAuthHeaders()
        });

        if (!res.ok) {
            const errorText = await res.text();
            console.error('API: Error response:', errorText);
            throw new Error(`Failed to fetch organization users: ${res.status} ${res.statusText}`);
        }

        const data = await res.json();
        return data;
    },

    inviteOrganizationUser: async (organizationId, inviteData) => {
        const res = await fetch(`${config.api.url}/organization/${organizationId}/invite`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(inviteData)
        });
        const data = await handleResponse(res);
        if (!data || typeof data !== 'object') {
            return normalizeOrganizationInviteResult(data);
        }
        return {
            ...data,
            ...normalizeOrganizationInviteResult(data),
        };
    },

    /**
     * Org-scoped: update a member's organization role (Admin / Midwife / Staff).
     * Caller must be an organization admin for this organization.
     */
    updateOrganizationUserOrganizationRole: async (organizationId, userId, organizationRole) => {
        const uid = userId != null ? encodeURIComponent(String(userId)) : '';
        const res = await fetch(
            `${config.api.url}/organization/${organizationId}/users/${uid}/organization-role`,
            {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ organizationRole })
            }
        );
        return handleResponse(res);
    },

    // Medical History APIs
    fetchMedicalHistory: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/medical-history`, {
            headers: getAuthHeaders()
        });
        if (res.status === 404) {
            throw new Error('Medical history not found');
        }
        return handleResponse(res);
    },

    createMedicalHistory: async (referenceId, medicalHistoryData) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/medical-history`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(medicalHistoryData)
        });
        if (res.status === 400) {
            const errorText = await res.text();
            throw new Error(errorText || 'Medical history already exists. Use update instead.');
        }
        return handleResponse(res);
    },

    updateMedicalHistory: async (referenceId, medicalHistoryData) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/medical-history`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(medicalHistoryData)
        });
        if (res.status === 404) {
            throw new Error('Medical history not found. Create one first.');
        }
        return handleResponse(res);
    },

    deleteMedicalHistory: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/medical-history`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    getMedicalHistoryStatus: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/medical-history/status`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Client Medical Profile — Conditions
    getClientConditions: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/conditions`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },
    createClientCondition: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/conditions`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    updateClientCondition: async (clientRef, conditionRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/conditions/${conditionRef}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    deleteClientCondition: async (clientRef, conditionRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/conditions/${conditionRef}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Surgeries
    getClientSurgeries: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/surgeries`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },
    createClientSurgery: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/surgeries`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    updateClientSurgery: async (clientRef, surgeryRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/surgeries/${surgeryRef}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    deleteClientSurgery: async (clientRef, surgeryRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/surgeries/${surgeryRef}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Family History
    getClientFamilyHistory: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/family`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },
    createClientFamilyHistory: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/family`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    updateClientFamilyHistory: async (clientRef, historyRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/family/${historyRef}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    deleteClientFamilyHistory: async (clientRef, historyRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/family/${historyRef}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Pregnancy Outcomes
    getPregnancyOutcomes: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/outcomes`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },
    createPregnancyOutcome: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/outcomes`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    updatePregnancyOutcome: async (clientRef, outcomeRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/outcomes/${outcomeRef}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },
    deletePregnancyOutcome: async (clientRef, outcomeRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/outcomes/${outcomeRef}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Obstetric Summary
    getObstetricSummary: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/obstetric-summary`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },
    upsertObstetricSummary: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/obstetric-summary`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    // Pregnancy Medical — Conditions
    getPregnancyConditions: async (pregnancyRef) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/medical/conditions`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Pregnancy Medical — Risk Flags
    getPregnancyRiskFlags: async (pregnancyRef) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/medical/risk-flags`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Pregnancy Medical — Conception Detail (UpsertPregnancyConceptionDetailRequest)
    getConceptionDetail: async (pregnancyRef) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/medical/conception-detail`, {
            headers: getAuthHeaders()
        });
        if (res.status === 204) return null;
        const data = await handleResponse(res);
        const toDateOnly = (v) => {
            if (!v) return '';
            const s = String(v);
            return s.includes('T') ? s.split('T')[0] : s;
        };
        const assistanceTypeToConceptionMethod = {
            Unassisted: 'Natural', IVF: 'IVF', IUI: 'IUI', DonorEgg: 'Donor Egg',
            DonorSperm: 'Donor Sperm', Surrogacy: 'Surrogacy', Other: 'Other', NotApplicable: 'Natural'
        };
        const assistanceType = data.assistanceType ?? data.AssistanceType ?? '';
        if (data && typeof data === 'object') {
            return {
                conceptionMethod: assistanceTypeToConceptionMethod[assistanceType] ?? assistanceType ?? '',
                lmpDate: toDateOnly(data.lmpDate ?? data.LmpDate ?? ''),
                ovulationDate: toDateOnly(data.ovulationDate ?? data.OvulationDate ?? ''),
                conceptionDate: toDateOnly(data.conceptionOrTransferDateUtc ?? data.ConceptionOrTransferDateUtc ?? ''),
                notes: data.notes ?? data.Notes ?? '',
                fertilityClinicOrProvider: data.fertilityClinicOrProvider ?? data.FertilityClinicOrProvider ?? '',
                conceptionMedicationsUsed: data.conceptionMedicationsUsed ?? data.ConceptionMedicationsUsed ?? '',
                embryoCount: data.embryoCount ?? data.EmbryoCount ?? null,
                multipleGestationConfirmed: data.multipleGestationConfirmed ?? data.MultipleGestationConfirmed ?? null,
                ivfTransferType: data.ivfTransferType ?? data.IvfTransferType ?? '',
                positivePregnancyTestDate: toDateOnly(data.positivePregnancyTestDate ?? data.PositivePregnancyTestDate ?? ''),
            };
        }
        return data;
    },
    updateConceptionDetail: async (pregnancyRef, payload) => {
        const conceptionMethodToAssistanceType = {
            Natural: 'Unassisted', IVF: 'IVF', IUI: 'IUI', 'Donor Egg': 'DonorEgg',
            'Donor Sperm': 'DonorSperm', Surrogacy: 'Surrogacy', Other: 'Other'
        };
        const assistanceType = conceptionMethodToAssistanceType[payload.conceptionMethod] ?? 'Unassisted';
        const toIso = (d) => {
            if (!d) return null;
            const s = String(d).trim();
            return s.includes('T') ? s : `${s}T00:00:00.000Z`;
        };
        const apiPayload = {
            assistanceType,
            ivfTransferType: payload.ivfTransferType ?? 'NotApplicable',
            conceptionOrTransferDateUtc: toIso(payload.conceptionDate),
            lmpDate: toIso(payload.lmpDate),
            ovulationDate: toIso(payload.ovulationDate),
            fertilityClinicOrProvider: payload.fertilityClinicOrProvider ?? null,
            conceptionMedicationsUsed: payload.conceptionMedicationsUsed ?? null,
            embryoCount: payload.embryoCount ?? null,
            multipleGestationConfirmed: payload.multipleGestationConfirmed ?? null,
            positivePregnancyTestDate: payload.positivePregnancyTestDate ? toIso(payload.positivePregnancyTestDate) : null,
        };
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/medical/conception-detail`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(apiPayload)
        });
        return handleNoContentResponse(res);
    },

    // Pregnancy — EDD Estimates (multi-method due date comparison)
    // Backend spec: docs/BACKEND_PREGNANCY_EDD_ESTIMATES_API.md
    getEddEstimates: async (pregnancyRef) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/edd-estimates`, {
            headers: getAuthHeaders()
        });
        const data = await handleResponse(res);
        if (!data || typeof data !== 'object') return data;
        const toDateOnly = (v) => {
            if (!v) return null;
            const s = String(v);
            return s.includes('T') ? s.split('T')[0] : s;
        };
        return {
            lmpDate:                         toDateOnly(data.lmpDate                         ?? data.LmpDate),
            lmpEdd:                          toDateOnly(data.lmpEdd                          ?? data.LmpEdd),
            lmpCycleLengthDays:              data.lmpCycleLengthDays                         ?? data.LmpCycleLengthDays
                                                ?? data.cycleLengthDays                       ?? data.CycleLengthDays               ?? null,
            ovulationDate:                   toDateOnly(data.ovulationDate                   ?? data.OvulationDate),
            ovulationEdd:                    toDateOnly(data.ovulationEdd                    ?? data.OvulationEdd),
            ultrasound1ExamDate:             toDateOnly(data.ultrasound1ExamDate             ?? data.Ultrasound1ExamDate),
            ultrasound1GestationalAgeWeeks:  data.ultrasound1GestationalAgeWeeks             ?? data.Ultrasound1GestationalAgeWeeks  ?? null,
            ultrasound1GestationalAgeDays:   data.ultrasound1GestationalAgeDays              ?? data.Ultrasound1GestationalAgeDays   ?? null,
            ultrasound1Edd:                  toDateOnly(data.ultrasound1Edd                  ?? data.Ultrasound1Edd),
            ultrasound2ExamDate:             toDateOnly(data.ultrasound2ExamDate             ?? data.Ultrasound2ExamDate),
            ultrasound2GestationalAgeWeeks:  data.ultrasound2GestationalAgeWeeks             ?? data.Ultrasound2GestationalAgeWeeks  ?? null,
            ultrasound2GestationalAgeDays:   data.ultrasound2GestationalAgeDays              ?? data.Ultrasound2GestationalAgeDays   ?? null,
            ultrasound2Edd:                  toDateOnly(data.ultrasound2Edd                  ?? data.Ultrasound2Edd),
            customEdd:                       toDateOnly(data.customEdd                       ?? data.CustomEdd),
            customEddNote:                   data.customEddNote                              ?? data.CustomEddNote                   ?? '',
            selectedMethod:                  data.selectedMethod                             ?? data.SelectedMethod                  ?? null,
        };
    },
    updateEddEstimates: async (pregnancyRef, payload) => {
        const toIsoDateOnly = (d) => {
            if (!d) return null;
            const s = String(d).trim();
            return s.includes('T') ? s.split('T')[0] : s;
        };
        const apiPayload = {
            lmpDate:                        toIsoDateOnly(payload.lmpDate),
            lmpCycleLengthDays:             payload.lmpCycleLengthDays ?? null,
            ovulationDate:                  toIsoDateOnly(payload.ovulationDate),
            ultrasound1ExamDate:            toIsoDateOnly(payload.ultrasound1ExamDate),
            ultrasound1GestationalAgeWeeks: payload.ultrasound1GestationalAgeWeeks ?? null,
            ultrasound1GestationalAgeDays:  payload.ultrasound1GestationalAgeDays  ?? null,
            ultrasound1Edd:                 toIsoDateOnly(payload.ultrasound1Edd),
            ultrasound2ExamDate:            toIsoDateOnly(payload.ultrasound2ExamDate),
            ultrasound2GestationalAgeWeeks: payload.ultrasound2GestationalAgeWeeks ?? null,
            ultrasound2GestationalAgeDays:  payload.ultrasound2GestationalAgeDays  ?? null,
            ultrasound2Edd:                 toIsoDateOnly(payload.ultrasound2Edd),
            customEdd:                      toIsoDateOnly(payload.customEdd),
            customEddNote:                  payload.customEddNote ?? null,
            selectedMethod:                 payload.selectedMethod ?? null,
        };
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/edd-estimates`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(apiPayload),
        });
        return handleNoContentResponse(res);
    },

    syncPregnancyLmp: async (pregnancyRef, { lmpDate, lmpCycleLengthDays, selectedMethod, syncConception = false } = {}) => {
        const normalizedLmpDate = toDateOnlyString(lmpDate);
        const shouldClear = lmpDate === '' || lmpDate === null;
        if (!pregnancyRef || (!normalizedLmpDate && !shouldClear)) return null;

        const [currentEdd, currentConception] = await Promise.all([
            api.getEddEstimates(pregnancyRef).catch(() => ({})),
            syncConception ? api.getConceptionDetail(pregnancyRef).catch(() => null) : Promise.resolve(null),
        ]);

        const hasCycleLength = lmpCycleLengthDays !== undefined;
        const mergedEdd = {
            ...(currentEdd || {}),
            lmpDate: normalizedLmpDate,
            lmpCycleLengthDays: normalizedLmpDate && hasCycleLength
                ? toOptionalNumber(lmpCycleLengthDays)
                : (normalizedLmpDate ? (currentEdd?.lmpCycleLengthDays ?? null) : null),
            selectedMethod: normalizedLmpDate ? (selectedMethod ?? currentEdd?.selectedMethod ?? null) : (
                currentEdd?.selectedMethod === 'Lmp' ? null : currentEdd?.selectedMethod ?? null
            ),
        };

        await api.updateEddEstimates(pregnancyRef, mergedEdd);

        if (syncConception && (normalizedLmpDate || currentConception)) {
            await api.updateConceptionDetail(pregnancyRef, {
                ...(currentConception || {}),
                conceptionMethod: currentConception?.conceptionMethod || 'Natural',
                lmpDate: normalizedLmpDate,
            });
        }

        return mergedEdd;
    },

    // Pregnancy Medical — Baseline Snapshots
    getBaselineSnapshots: async (pregnancyRef) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRef}/medical/snapshots`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Client Medical Profile — Intake Entities (GET + PUT upsert)
    getMenstrualHistory: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/menstrual-history`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },
    getPreventiveScreening: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/preventive-screening`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },
    getSocialHistory: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/social-history`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },
    getFoodFinancialSecurity: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/food-financial-security`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },
    getCulturalSocialProfile: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/cultural-social-profile`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },
    getMedicationIntake: async (clientRef) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/medication-intake`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },

    upsertMenstrualHistory: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/menstrual-history`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Preventive Screening (upsert)
    upsertPreventiveScreening: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/preventive-screening`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Social History (upsert)
    upsertSocialHistory: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/social-history`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Food & Financial Security (upsert)
    upsertFoodFinancialSecurity: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/food-financial-security`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Cultural & Social Profile (upsert)
    upsertCulturalSocialProfile: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/cultural-social-profile`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    // Client Medical Profile — Medication Intake (upsert)
    upsertMedicationIntake: async (clientRef, payload) => {
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-profile/medication-intake`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload)
        });
        return handleNoContentResponse(res);
    },

    getStructuredMedicalProfileStatus: async (clientRef) => {
        const readSection = async (key, loader, emptyValue) => {
            try {
                return [key, await loader()];
            } catch {
                return [key, emptyValue];
            }
        };

        const entries = await Promise.all([
            readSection('conditions', () => api.getClientConditions(clientRef), []),
            readSection('surgeries', () => api.getClientSurgeries(clientRef), []),
            readSection('familyHistory', () => api.getClientFamilyHistory(clientRef), []),
            readSection('outcomes', () => api.getPregnancyOutcomes(clientRef), []),
            readSection('menstrualHistory', () => api.getMenstrualHistory(clientRef), null),
            readSection('preventiveScreening', () => api.getPreventiveScreening(clientRef), null),
            readSection('socialHistory', () => api.getSocialHistory(clientRef), null),
            readSection('foodFinancialSecurity', () => api.getFoodFinancialSecurity(clientRef), null),
            readSection('culturalSocialProfile', () => api.getCulturalSocialProfile(clientRef), null),
            readSection('medicationIntake', () => api.getMedicationIntake(clientRef), null),
            readSection('medications', () => medicationApi.getCurrentMedications(clientRef), []),
        ]);

        return deriveStructuredMedicalProfileStatus(Object.fromEntries(entries));
    },

    // Medical History Intake — batch submission from client wizard
    submitMedicalHistoryIntake: async ({ clientRef, pregnancyRef, intakeState }) => {
        const results = {
            conditions: [], surgeries: [], familyHistory: [],
            outcomes: [], medications: [], errors: []
        };

        const safeCall = async (label, fn) => {
            try { return await fn(); }
            catch (err) { results.errors.push({ label, error: err.message }); return null; }
        };

        const normalize = (value) => String(value || '').trim().toLowerCase();
        const addIfNew = (seen, key, item) => {
            const normalizedKey = normalize(key);
            if (!normalizedKey || seen.has(normalizedKey)) return null;
            seen.add(normalizedKey);
            return item;
        };
        const outcomeDedupeKey = (o) => {
            const outcomeDate = o.outcomeDate ? String(o.outcomeDate).split('T')[0] : (o.year || '');
            return normalize(`${o.outcomeType}|${outcomeDate}|${o.gestationalAgeWeeks || ''}`);
        };
        const withoutOutcomeReference = (outcome) => {
            const payload = { ...outcome };
            delete payload.outcomeReferenceId;
            return payload;
        };

        const [existingConditions, existingSurgeries, existingFamilyHistory, existingOutcomes, existingMedications] = await Promise.all([
            api.getClientConditions(clientRef).catch(() => []),
            api.getClientSurgeries(clientRef).catch(() => []),
            api.getClientFamilyHistory(clientRef).catch(() => []),
            api.getPregnancyOutcomes(clientRef).catch(() => []),
            medicationApi.getCurrentMedications(clientRef).catch(() => []),
        ]);

        const conditionNames = new Set((Array.isArray(existingConditions) ? existingConditions : []).map((c) => normalize(c.conditionName)));
        const surgeryKeys = new Set((Array.isArray(existingSurgeries) ? existingSurgeries : []).map((s) => normalize(`${s.surgeryType}|${s.year || ''}`)));
        const familyKeys = new Set((Array.isArray(existingFamilyHistory) ? existingFamilyHistory : []).map((f) => normalize(`${f.conditionName}|${f.relationship || 'Other'}`)));
        const outcomeKeys = new Set((Array.isArray(existingOutcomes) ? existingOutcomes : []).map(outcomeDedupeKey));

        const newConditions = (intakeState.conditions || [])
            .map((c) => addIfNew(conditionNames, c.conditionName, c))
            .filter(Boolean);
        const newSurgeries = (intakeState.surgeries || [])
            .map((s) => addIfNew(surgeryKeys, `${s.surgeryType}|${s.year || ''}`, s))
            .filter(Boolean);
        const newFamilyHistory = (intakeState.familyHistory || [])
            .map((f) => addIfNew(familyKeys, `${f.conditionName}|${f.relationship || 'Other'}`, f))
            .filter(Boolean);
        const requestedOutcomes = intakeState.pastPregnancyOutcomes || [];
        const newOutcomes = requestedOutcomes
            .filter((o) => !o.outcomeReferenceId)
            .map((o) => addIfNew(outcomeKeys, outcomeDedupeKey(o), o))
            .filter(Boolean);
        const outcomeUpdates = requestedOutcomes.filter((o) => o.outcomeReferenceId);
        const newMedications = buildMedicationPayloadsFromIntake({
            ...intakeState.medicationIntake,
            medicationRecords: intakeState.medicationRecords,
        }, existingMedications);

        logMedicalHistoryDebug('submitMedicalHistoryIntake dedupe', {
            clientRef,
            existing: {
                conditions: existingConditions,
                surgeries: existingSurgeries,
                familyHistory: existingFamilyHistory,
                outcomes: existingOutcomes,
                medications: existingMedications,
            },
            requested: {
                conditions: intakeState.conditions || [],
                surgeries: intakeState.surgeries || [],
                familyHistory: intakeState.familyHistory || [],
                outcomes: intakeState.pastPregnancyOutcomes || [],
                medicationIntake: intakeState.medicationIntake || null,
            },
            willCreate: {
                conditions: newConditions,
                surgeries: newSurgeries,
                familyHistory: newFamilyHistory,
                outcomes: newOutcomes,
                outcomeUpdates,
                medications: newMedications,
            },
        });

        // ── Phase 1: All independent create / upsert operations (parallel) ─────
        const conditionPromises = newConditions.map((c) =>
            safeCall(`Condition: ${c.conditionName}`, () =>
                api.createClientCondition(clientRef, { conditionName: c.conditionName, isActive: true, category: c.category || 'Other', notes: c.notes || null })
            )
        );
        const surgeryPromises = newSurgeries.map((s) =>
            safeCall(`Surgery: ${s.surgeryType}`, () =>
                api.createClientSurgery(clientRef, { surgeryType: s.surgeryType, year: s.year ? Number(s.year) : null, complications: s.complications || null })
            )
        );
        const familyPromises = newFamilyHistory.map((f) =>
            safeCall(`Family: ${f.conditionName}`, () =>
                api.createClientFamilyHistory(clientRef, { conditionName: f.conditionName, relationship: f.relationship || 'Other', notes: f.notes || null })
            )
        );
        const outcomePromises = newOutcomes.map((o) =>
            safeCall(`Outcome: ${o.outcomeType}`, () =>
                api.createPregnancyOutcome(clientRef, withoutOutcomeReference(o))
            )
        );
        const outcomeUpdatePromises = outcomeUpdates.map((o) =>
            safeCall(`Outcome: ${o.outcomeType}`, () =>
                api.updatePregnancyOutcome(clientRef, o.outcomeReferenceId, withoutOutcomeReference(o))
            )
        );
        const medicationPromises = newMedications.map((med) =>
            safeCall(`Medication: ${med.CustomMedicationName}`, () => medicationApi.createMedication(clientRef, med))
        );

        // Groups 2 & 3: new 1:1 upsert endpoints
        const upsertPromises = [
            safeCall('Menstrual history',      () => api.upsertMenstrualHistory(clientRef,      intakeState.menstrualHistory)),
            safeCall('Preventive screening',   () => api.upsertPreventiveScreening(clientRef,   intakeState.preventiveScreening)),
            safeCall('Social history',         () => api.upsertSocialHistory(clientRef,         intakeState.socialHistory)),
            safeCall('Food & financial',       () => api.upsertFoodFinancialSecurity(clientRef, intakeState.foodFinancialSecurity)),
            safeCall('Cultural social profile',() => api.upsertCulturalSocialProfile(clientRef, intakeState.culturalSocialProfile)),
            safeCall('Medication intake',      () => api.upsertMedicationIntake(clientRef,      intakeState.medicationIntake)),
        ];

        const allBatchResults = await Promise.all([
            ...conditionPromises, ...surgeryPromises, ...familyPromises,
            ...outcomePromises, ...outcomeUpdatePromises, ...medicationPromises, ...upsertPromises,
        ]);

        let offset = 0;
        results.conditions        = allBatchResults.slice(offset, offset += conditionPromises.length).filter(Boolean);
        results.surgeries         = allBatchResults.slice(offset, offset += surgeryPromises.length).filter(Boolean);
        results.familyHistory     = allBatchResults.slice(offset, offset += familyPromises.length).filter(Boolean);
        results.outcomes          = allBatchResults.slice(offset, offset += outcomePromises.length + outcomeUpdatePromises.length).filter(Boolean);
        results.medications       = allBatchResults.slice(offset, offset += medicationPromises.length).filter(Boolean);

        // ── Phase 2: Operations requiring a prior fetch (sequential, independent) ─
        await Promise.all([
            // Group 1: Client entity fields — fetch current, merge, update
            safeCall('Client intake fields', async () => {
                if (!intakeState.clientUpdates) return null;
                const current = await api.fetchClient(clientRef);
                return api.updateClient(clientRef, { ...current, ...intakeState.clientUpdates });
            }),

            // Group 1: Pregnancy entity fields — fetch current, merge, update
            pregnancyRef && intakeState.pregnancyUpdates
                ? safeCall('Pregnancy intake fields', async () => {
                    const pregnancies = await api.fetchPregnancies(clientRef);
                    const current = (pregnancies || []).find((p) => p.referenceId === pregnancyRef);
                    if (!current) return null;
                    const merged = {
                        dueDate: current.dueDate,
                        dueDateCalculationMethod: current.dueDateCalculationMethod,
                        notes: current.notes || null,
                        isTwins: current.isTwins || false,
                        placentationType: current.placentationType || null,
                        enableAutoDocumentSend: current.enableAutoDocumentSend || false,
                        ...intakeState.pregnancyUpdates,
                    };
                    return api.updatePregnancy(current.id, merged);
                  })
                : Promise.resolve(null),

            // Section 4: Conception detail (no prior fetch needed)
            pregnancyRef && intakeState.conceptionDetail?.conceptionMethod
                ? safeCall('Conception detail', () =>
                    api.updateConceptionDetail(pregnancyRef, intakeState.conceptionDetail)
                  )
                : Promise.resolve(null),

            pregnancyRef && intakeState.lmpDate
                ? safeCall('LMP sync', () =>
                    api.syncPregnancyLmp(pregnancyRef, {
                        lmpDate: intakeState.lmpDate,
                        lmpCycleLengthDays: intakeState.lmpCycleLengthDays,
                    })
                  )
                : Promise.resolve(null),
        ]);

        return results;
    },

    completeMedicalHistoryIntake: async (clientRef) => {
        if (!clientRef) throw new Error('Client reference ID is required');
        const res = await fetch(`${config.api.url}/clients/${clientRef}/medical-history-intake/complete`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        return handleResponse(res);
    }
};

export default api;

// Document & Handout APIs
export { consentApi } from './consentApi';
export { handoutApi } from './handoutApi';
export { default as formsApi } from './formsApi';
export { default as systemAnnouncementsApi } from './systemAnnouncementsApi';
export { default as practiceDocumentsApi } from './practiceDocumentsApi';
export { PRACTICE_DOCUMENT_TYPES, PRACTICE_DOCUMENT_TYPE_LABELS } from './practiceDocumentsApi';
export { default as lendingLibraryApi, lendingLibraryQueryKeys, LENDING_LIBRARY_CONDITIONS } from './lendingLibraryApi';
